// ================================================================
// src/core/books/data.js
// ================================================================
// لایه‌ی داده و موجودیت‌های سیستم کتاب‌های داینامیک
// کاملاً روی engine (storage.js) سوار می‌شه
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. تعریف همه‌ی موجودیت‌ها (Teacher, Request, Project, Version, ...)
//   ۲. Factory برای هر موجودیت با defaults و _id یکتا
//   ۳. Validators کامل با پیام خطای دقیق
//   ۴. Registries: CRUD + Indexes + State Machines
//   ۵. Query helpers برای مصرف‌کننده‌های UI
//   ۶. Migration از داده‌های legacy (BOOKS_DATA, TEACHERS_DATA)
//   ۷. Idempotency + Uniqueness + Immutability Guards
// ================================================================

import { engine, utils, constants } from './storage.js';
import {
  GRADE_LIST,
  GRADE_TITLES as CUR_GRADE_TITLES,
  SUBJECT_LABELS as CUR_SUBJECT_LABELS,
  SUBJECTS_BY_GRADE as CUR_SUBJECTS_BY_GRADE,
  SUBJECTS,
  FIELDS,
  FIELD_LIST,
  FIELD_IDS,
  normalizeGradeId,
  normalizeFieldId,
  normalizeSubjectId,
  getGrade,
  getField,
  getSubject,
  getSubjectTitle,
  getBookTitle,
  isHighSchool,
  needsField,
  getSubjectIdsFor,
  getSubjectsFor,
  getStageForGrade,
} from '../education/curriculum.js';

const { KEYS } = constants;

// ================================================================
// SECTION 1: ENUMS, LABELS, TRANSITIONS
// ================================================================

export const DATA_SCHEMA = '2.0.0';

// ================================================================
// CURRICULUM-AWARE CONSTANTS
// ================================================================
// همه‌ی این‌ها از curriculum.js می‌آن تا یک منبع حقیقت داشته باشیم
// ----------------------------------------------------------------

// لیست شناسه‌های پایه — ۱ تا ۱۲
export const GRADES = Object.freeze(GRADE_LIST.map((g) => g.id));

// عناوین فارسی پایه‌ها (۱ تا ۱۲)
export const GRADE_TITLES = CUR_GRADE_TITLES;

// برچسب‌های دروس (شامل هر ۲۹ درس اصلی + ۲ deprecated)
export const SUBJECT_LABELS = CUR_SUBJECT_LABELS;

// دروس هر پایه — برای ۱ تا ۹ ساختار ساده، برای ۱۰ تا ۱۲ باید از
// getSubjectIdsFor(grade, field) استفاده کنی
export const SUBJECTS_BY_GRADE = CUR_SUBJECTS_BY_GRADE;

// ================================================================
// RE-EXPORTS (برای مصرف‌کننده‌های logic.js و UI)
// ================================================================
export {
  FIELDS,
  FIELD_LIST,
  FIELD_IDS,
  SUBJECTS,
  getGrade,
  getField,
  getSubject,
  getSubjectTitle,
  getBookTitle,
  isHighSchool,
  needsField,
  getSubjectIdsFor,
  getSubjectsFor,
  getStageForGrade,
};

export const REQUEST_STATUS = Object.freeze({
  DRAFT: 'draft',
  PENDING: 'pending',
  EDIT_REQUESTED: 'edit-requested',
  REJECTED: 'rejected',
  APPROVED: 'approved',
  WITHDRAWN: 'withdrawn',
});

export const PROJECT_STATE = Object.freeze({
  BUILDING: 'building',
  PENDING_INITIAL: 'pending-initial',
  PUBLISHED: 'published',
  UPDATE_PENDING: 'update-pending',
  SUSPENDED: 'suspended',
});

export const VERSION_STATUS = Object.freeze({
  DRAFT: 'draft',
  PENDING: 'pending',
  PUBLISHED: 'published',
  SUPERSEDED: 'superseded',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
});

export const STEP_TYPE = Object.freeze({
  LESSON: 'lesson',
  QUIZ: 'quiz',
});

// state machines — تنها transitionهای مجاز
export const PROJECT_TRANSITIONS = Object.freeze({
  [PROJECT_STATE.BUILDING]: [PROJECT_STATE.PENDING_INITIAL, PROJECT_STATE.SUSPENDED],
  [PROJECT_STATE.PENDING_INITIAL]: [PROJECT_STATE.BUILDING, PROJECT_STATE.PUBLISHED, PROJECT_STATE.SUSPENDED],
  [PROJECT_STATE.PUBLISHED]: [PROJECT_STATE.UPDATE_PENDING, PROJECT_STATE.SUSPENDED],
  [PROJECT_STATE.UPDATE_PENDING]: [PROJECT_STATE.PUBLISHED, PROJECT_STATE.SUSPENDED],
  [PROJECT_STATE.SUSPENDED]: [PROJECT_STATE.BUILDING, PROJECT_STATE.PUBLISHED],
});

export const REQUEST_TRANSITIONS = Object.freeze({
  [REQUEST_STATUS.DRAFT]: [REQUEST_STATUS.PENDING, REQUEST_STATUS.WITHDRAWN],
  [REQUEST_STATUS.PENDING]: [
    REQUEST_STATUS.APPROVED, REQUEST_STATUS.REJECTED,
    REQUEST_STATUS.EDIT_REQUESTED, REQUEST_STATUS.WITHDRAWN,
  ],
  [REQUEST_STATUS.EDIT_REQUESTED]: [REQUEST_STATUS.DRAFT, REQUEST_STATUS.PENDING, REQUEST_STATUS.WITHDRAWN],
  [REQUEST_STATUS.REJECTED]: [REQUEST_STATUS.DRAFT],
  [REQUEST_STATUS.APPROVED]: [],
  [REQUEST_STATUS.WITHDRAWN]: [],
});

// ================================================================
// SECTION 2: ID GENERATORS
// ================================================================

const ID_PREFIXES = Object.freeze({
  TEACHER: 'tp', REQUEST: 'rq', PROJECT: 'pj', VERSION: 'vr',
  CHAPTER: 'ch', LESSON: 'ls', STEP: 'st', VIDEO: 'vd',
});

const genId = (p) => `${p}_${utils.shortId()}`;

export const genTeacherId  = () => genId(ID_PREFIXES.TEACHER);
export const genRequestId  = () => genId(ID_PREFIXES.REQUEST);
export const genProjectId  = () => genId(ID_PREFIXES.PROJECT);
export const genVersionId  = () => genId(ID_PREFIXES.VERSION);
export const genChapterId  = () => genId(ID_PREFIXES.CHAPTER);
export const genLessonId   = () => genId(ID_PREFIXES.LESSON);
export const genStepId     = () => genId(ID_PREFIXES.STEP);
export const genVideoId    = () => genId(ID_PREFIXES.VIDEO);

// ================================================================
// SECTION 3: FOLDER NAME (یکتا در کل سیستم، تغییرناپذیر بعد از انتشار)
// ================================================================



// ================================================================
// 🆕 SECTION 2.5: FREE STUDY VIDEO
// ================================================================
export const genFreeStudyVideoId = () => `fvd_${utils.shortId()}`;

export function createFreeStudyVideo(input = {}) {
  return {
    _id: input._id || genFreeStudyVideoId(),
    blobId: input.blobId || null,
    externalUrl: input.externalUrl || null,
    thumbnailBlobId: input.thumbnailBlobId || null,
    title: input.title || '',
    description: input.description || '',
    durationMinutes: Number(input.durationMinutes) || 0,
    price: Number(input.price) || 0,
    isFree: input.isFree === true,
    order: Number.isFinite(input.order) ? input.order : 0,
    archivedAt: input.archivedAt || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
  };
}

const cloneFreeStudyVideo = (v) => (v ? createFreeStudyVideo(v) : null);

// ================================================================
// SECTION 3: FOLDER NAME
// ================================================================















const FOLDER_REGEX = /^[A-Za-z][A-Za-z0-9-]{2,63}$/;
const FOLDER_RESERVED = new Set([
  'default', 'system', 'null', 'undefined', 'admin', 'root',
  'test', 'temp', 'tmp', 'guest', 'public', 'private', 'user', 'teacher',
]);

const PERSIAN_TRANSLIT = {
  'ا':'A','آ':'A','ب':'B','پ':'P','ت':'T','ث':'S','ج':'J','چ':'Ch',
  'ح':'H','خ':'Kh','د':'D','ذ':'Z','ر':'R','ز':'Z','ژ':'Zh','س':'S',
  'ش':'Sh','ص':'S','ض':'Z','ط':'T','ظ':'Z','ع':'A','غ':'Gh','ف':'F',
  'ق':'Gh','ک':'K','گ':'G','ل':'L','م':'M','ن':'N','و':'V','ه':'H',
  'ی':'Y','ئ':'Y','ء':'','ً':'','ٌ':'','ٍ':'','َ':'','ُ':'','ِ':'',
  'ّ':'','ْ':'','۰':'0','۱':'1','۲':'2','۳':'3','۴':'4','۵':'5',
  '۶':'6','۷':'7','۸':'8','۹':'9',
};

function transliterate(str) {
  let out = '';
  for (const ch of String(str || '')) {
    if (PERSIAN_TRANSLIT[ch] !== undefined) out += PERSIAN_TRANSLIT[ch];
    else if (/[A-Za-z0-9]/.test(ch)) out += ch;
    else if (ch === ' ' || ch === '-' || ch === '_') out += '-';
  }
  return out;
}

export function validateFolderName(folderName) {
  const errors = [];
  if (!folderName || typeof folderName !== 'string') {
    return { ok: false, errors: ['folder_name_required'] };
  }
  if (folderName.length < 3) errors.push('folder_name_too_short');
  if (folderName.length > 64) errors.push('folder_name_too_long');
  if (!FOLDER_REGEX.test(folderName)) errors.push('folder_name_invalid_format');
  if (FOLDER_RESERVED.has(folderName.toLowerCase())) errors.push('folder_name_reserved');
  return { ok: errors.length === 0, errors };
}

/**
 * تولید folderName یکتا بر اساس نام کامل (با transliteration فارسی → لاتین)
 * @param {string} fullName
 * @returns {string} folderName یکتا در کل سیستم
 */
export function generateFolderName(fullName) {
  const trans = transliterate(fullName || 'Teacher');
  let base = trans
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('-')
    .replace(/[^A-Za-z0-9-]/g, '')
    .slice(0, 50);

  if (!base || !/^[A-Za-z]/.test(base)) base = 'Teacher';

  for (let i = 0; i < 20; i++) {
    const suffix = Math.floor(1000 + Math.random() * 9000);
    const candidate = `${base}-${suffix}`;
    if (!validateFolderName(candidate).ok) continue;
    if (!dataEngine.teacher.getByFolderName(candidate)) return candidate;
  }
  return `Teacher-${Date.now().toString(36)}`;
}

// ================================================================
// SECTION 4: ENTITY FACTORIES
// ================================================================

const now = () => Date.now();

export function createTeacherProfile(input = {}) {
  return {
    id: input.id || genTeacherId(),
    userId: input.userId || null,
    folderName: input.folderName || null,
    fullName: input.fullName || '',
    bio: input.bio || '',
    experienceYears: Number(input.experienceYears) || 0,
    specialties: Array.isArray(input.specialties) ? [...input.specialties] : [],
    avatarBlobId: input.avatarBlobId || null,
    certificateBlobIds: Array.isArray(input.certificateBlobIds) ? [...input.certificateBlobIds] : [],
    socialLinks: input.socialLinks ? { ...input.socialLinks } : {},
    isActive: input.isActive !== false,
    isLegacy: !!input.isLegacy,
    legacyId: input.legacyId || null,
    // فیلدهای legacy (فقط برای داده‌های مهاجرت‌شده)
    legacyAvatar: input.legacyAvatar || null,
    legacyStars: input.legacyStars != null ? Number(input.legacyStars) : null,
    legacyStudentsRated: input.legacyStudentsRated != null ? Number(input.legacyStudentsRated) : null,
    legacyIsSpecial: !!input.legacyIsSpecial,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
    archivedAt: input.archivedAt || null,
    _v: DATA_SCHEMA,
  };
}

export function createTeacherRequest(input = {}) {
  return {
    id: input.id || genRequestId(),
    idempotencyKey: input.idempotencyKey || utils.uuid(),
    teacherId: input.teacherId || null,
    userId: input.userId || null,
    // 🔽 CHANGED: normalize خودکار پایه/رشته/درس
    grade: normalizeGradeId(input.grade) || (input.grade != null ? String(input.grade) : null),
    field: input.field ? (normalizeFieldId(input.field) || null) : null,
    subject: input.subject ? (normalizeSubjectId(input.subject) || input.subject) : null,
    proposedFolderName: input.proposedFolderName || null,
    formData: {
      fullName: input.formData?.fullName || '',
      bio: input.formData?.bio || '',
      experienceYears: Number(input.formData?.experienceYears) || 0,
      specialties: Array.isArray(input.formData?.specialties) ? [...input.formData.specialties] : [],
      avatarBlobId: input.formData?.avatarBlobId || null,
      certificateBlobIds: Array.isArray(input.formData?.certificateBlobIds)
        ? [...input.formData.certificateBlobIds] : [],
      socialLinks: input.formData?.socialLinks ? { ...input.formData.socialLinks } : {},
    },
    status: input.status || REQUEST_STATUS.DRAFT,
    adminNotes: input.adminNotes || '',
    adminId: input.adminId || null,
    relatedProjectId: input.relatedProjectId || null,
    history: Array.isArray(input.history) ? [...input.history] : [],
    submittedAt: input.submittedAt || null,
    reviewedAt: input.reviewedAt || null,
    withdrawnAt: input.withdrawnAt || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
    archivedAt: input.archivedAt || null,
    _v: DATA_SCHEMA,
  };
}

export function createProject(input = {}) {
  const type = input.type === 'freeStudy' ? 'freeStudy' : 'book';

  const base = {
    id: input.id || genProjectId(),
    type,
    teacherId: input.teacherId || null,
    title: input.title || '',
    coverImageBlobId: input.coverImageBlobId || input.image || null, // 🆕
    description: input.description || '',
    versions: Array.isArray(input.versions) ? [...input.versions] : [],
    draftVersionId: input.draftVersionId || null,
    pendingVersionId: input.pendingVersionId || null,
    publishedVersionId: input.publishedVersionId || null,
    state: input.state || PROJECT_STATE.BUILDING,

    subscriptionEnabled: input.subscriptionEnabled === true,
    subscriptionPrice: Number(input.subscriptionPrice) || 0,
    subscriptionDurationDays: Number(input.subscriptionDurationDays) || 365,
    subscriptionGraceDays: Number(input.subscriptionGraceDays) || 3,

    isLegacy: !!input.isLegacy,
    legacySource: input.legacySource || null,
    stats: {
      studentsCount: input.stats?.studentsCount || 0,
      avgProgress: input.stats?.avgProgress || 0,
      avgRating: input.stats?.avgRating || 0,
      ratingsCount: input.stats?.ratingsCount || 0,
      isSpecial: !!input.stats?.isSpecial,
      lastActivityAt: input.stats?.lastActivityAt || null,
    },
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
    publishedAt: input.publishedAt || null,
    suspendedAt: input.suspendedAt || null,
    archivedAt: input.archivedAt || null,
    _v: DATA_SCHEMA,
  };

  if (type === 'book') {
    return {
      ...base,
      grade: normalizeGradeId(input.grade) || (input.grade != null ? String(input.grade) : null),
      field: input.field ? (normalizeFieldId(input.field) || null) : null,
      subject: input.subject ? (normalizeSubjectId(input.subject) || input.subject) : null,
      folderName: input.folderName || null,
    };
  }

  // 🆕 مطالعه آزاد
  return {
    ...base,
    grade: null,
    field: null,
    subject: null,
    folderName: null,
    subtitle: input.subtitle || '',
    image: input.image || null,
    category: input.category || '',
    categoryId: input.categoryId || null, // 🆕
    level: input.level || 'مقدماتی',
    tags: Array.isArray(input.tags) ? [...input.tags] : [],
    color: input.color || '#4f46e5',
    currency: input.currency || 'IRT',
    fullCoursePrice: Number(input.fullCoursePrice) || 0,
  };
}

export function createProjectVersion(input = {}) {
  return {
    id: input.id || genVersionId(),
    projectId: input.projectId || null,
    parentVersionId: input.parentVersionId || null,
    status: input.status || VERSION_STATUS.DRAFT,
    chapters: Array.isArray(input.chapters) ? input.chapters.map(cloneChapter) : [],
    videos: Array.isArray(input.videos) ? input.videos.map(cloneFreeStudyVideo) : [], // 🆕
    changelog: Array.isArray(input.changelog) ? [...input.changelog] : [],
    contentHash: input.contentHash || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
    createdBy: input.createdBy || null,
    publishedAt: input.publishedAt || null,
    publishedBy: input.publishedBy || null,
    reviewedAt: input.reviewedAt || null,
    reviewedBy: input.reviewedBy || null,
    rejectionReason: input.rejectionReason || null,
    adminNotes: input.adminNotes || '',
    teacherNote: input.teacherNote || '', // 🆕
    _v: DATA_SCHEMA,
  };
}

export function createChapter(input = {}) {
  return {
    _id: input._id || genChapterId(),
    title: input.title || '',
    description: input.description || '',
    order: Number.isFinite(input.order) ? input.order : 0,
    lessons: Array.isArray(input.lessons) ? input.lessons.map(cloneLesson) : [],
    archivedAt: input.archivedAt || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
  };
}

export function createLesson(input = {}) {
  return {
    _id: input._id || genLessonId(),
    title: input.title || '',
    description: input.description || '',
    imageBlobId: input.imageBlobId || null,
    order: Number.isFinite(input.order) ? input.order : 0,
    steps: Array.isArray(input.steps) ? input.steps.map(cloneStep) : [],
    legacyLessonId: input.legacyLessonId || null,
    isFree: input.isFree === true,
    archivedAt: input.archivedAt || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
  };
}

export function createStep(input = {}) {
  return {
    _id: input._id || genStepId(),
    type: input.type === STEP_TYPE.QUIZ ? STEP_TYPE.QUIZ : STEP_TYPE.LESSON,
    quizId: input.quizId || null,
    quizMeta: input.quizMeta && typeof input.quizMeta === 'object'
      ? {
          title: input.quizMeta.title || '',
          questionCount: Number(input.quizMeta.questionCount) || 0,
          duration: Number(input.quizMeta.duration) || 0,
          passThreshold: Number(input.quizMeta.passThreshold) || 12,
          maxAttempts: Number(input.quizMeta.maxAttempts) || 2,
          retryPrice: Number(input.quizMeta.retryPrice) || 10000,
        }
      : null,
    title: input.title || '',
    description: input.description || '',
    durationMinutes: Number(input.durationMinutes) || 0,
    order: Number.isFinite(input.order) ? input.order : 0,
    videos: Array.isArray(input.videos) ? input.videos.map(cloneVideoRef) : [],
    legacyStepIndex: Number.isFinite(input.legacyStepIndex) ? input.legacyStepIndex : null,
    archivedAt: input.archivedAt || null,
    createdAt: input.createdAt || now(),
    updatedAt: input.updatedAt || now(),
  };
}



export const isQuizStep = (step) => step?.type === STEP_TYPE.QUIZ;
export const isLessonStep = (step) => !step?.type || step.type === STEP_TYPE.LESSON;



export function createVideoRef(input = {}) {
  return {
    _id: input._id || genVideoId(),
    blobId: input.blobId || null,
    externalUrl: input.externalUrl || null,
    title: input.title || '',
    mimeType: input.mimeType || '',
    size: Number(input.size) || 0,
    durationSeconds: Number(input.durationSeconds) || 0,
    order: Number.isFinite(input.order) ? input.order : 0,
    archivedAt: input.archivedAt || null,
    createdAt: input.createdAt || now(),
  };
}

// deep clone helpers
const cloneChapter  = (ch) => (ch ? createChapter(ch) : null);
const cloneLesson   = (ls) => (ls ? createLesson(ls) : null);
const cloneStep     = (st) => (st ? createStep(st) : null);
const cloneVideoRef = (v)  => (v  ? createVideoRef(v) : null);

// ================================================================
// SECTION 5: VALIDATORS
// ================================================================

export function validateTeacherProfile(tp) {
  const errors = [];
  if (!tp) return { ok: false, errors: ['profile_required'] };
  if (!tp.userId) errors.push('user_id_required');
  if (!tp.folderName) errors.push('folder_name_required');
  else { const fv = validateFolderName(tp.folderName); if (!fv.ok) errors.push(...fv.errors); }
  if (!tp.fullName || !String(tp.fullName).trim()) errors.push('full_name_required');
  if (tp.fullName && String(tp.fullName).length > 100) errors.push('full_name_too_long');
  if (tp.bio && String(tp.bio).length > 2000) errors.push('bio_too_long');
  if (tp.experienceYears != null && (tp.experienceYears < 0 || tp.experienceYears > 80)) {
    errors.push('experience_years_out_of_range');
  }
  return { ok: errors.length === 0, errors };
}

export function validateTeacherRequest(rq) {
  const errors = [];
  if (!rq) return { ok: false, errors: ['request_required'] };
  if (!rq.teacherId && !rq.userId) errors.push('teacher_or_user_required');

  // grade: 1 تا 12
  if (!rq.grade) errors.push('grade_required');
  else if (!GRADES.includes(String(rq.grade))) errors.push('grade_invalid');

  // 🔽 CHANGED: field برای 10-12
  const gradeNeedsField = rq.grade && needsField(String(rq.grade));
  if (gradeNeedsField) {
    if (!rq.field) errors.push('field_required');
    else if (!FIELDS[rq.field]) errors.push('field_invalid');
  } else if (rq.field) {
    // پایه‌های 1-9 نباید field داشته باشن
    errors.push('field_not_allowed');
  }

  // subject: چک بر اساس ID یا Title (backward compat)
  if (!rq.subject) errors.push('subject_required');
  else {
    const sid = normalizeSubjectId(rq.subject);
    if (!sid) errors.push('subject_invalid');
  }

  if (!rq.proposedFolderName) errors.push('folder_name_required');
  else { const fv = validateFolderName(rq.proposedFolderName); if (!fv.ok) errors.push(...fv.errors); }
  if (!Object.values(REQUEST_STATUS).includes(rq.status)) errors.push('status_invalid');
  return { ok: errors.length === 0, errors };
}

export function validateProject(pj) {
  const errors = [];
  if (!pj) return { ok: false, errors: ['project_required'] };
  if (!pj.teacherId) errors.push('teacher_id_required');

  // 🆕 مطالعه آزاد
  if (pj.type === 'freeStudy') {
    if (!pj.title || !String(pj.title).trim()) errors.push('title_required');
    if (String(pj.title || '').length > 200) errors.push('title_too_long');
    if (pj.subtitle && String(pj.subtitle).length > 300) errors.push('subtitle_too_long');
    if (pj.description && String(pj.description).length > 5000) errors.push('description_too_long');
    if (pj.category && String(pj.category).length > 100) errors.push('category_too_long');
    if (pj.level && String(pj.level).length > 50) errors.push('level_too_long');
    if (Array.isArray(pj.tags) && pj.tags.length > 20) errors.push('tags_too_many');
    if (pj.fullCoursePrice != null) {
      const p = Number(pj.fullCoursePrice);
      if (!Number.isFinite(p) || p < 0) errors.push('price_invalid');
    }
    if (!Object.values(PROJECT_STATE).includes(pj.state)) errors.push('state_invalid');
    return { ok: errors.length === 0, errors };
  }

  // ─── کتاب (بدون تغییر) ───
  if (!pj.grade) errors.push('grade_required');
  else if (!GRADES.includes(String(pj.grade))) errors.push('grade_invalid');

  const gradeNeedsField = pj.grade && needsField(String(pj.grade));
  if (gradeNeedsField) {
    if (!pj.field) errors.push('field_required');
    else if (!FIELDS[pj.field]) errors.push('field_invalid');
  } else if (pj.field) {
    errors.push('field_not_allowed');
  }

  if (!pj.subject) errors.push('subject_required');
  else {
    const sid = normalizeSubjectId(pj.subject);
    if (!sid) errors.push('subject_invalid');
  }

  if (!pj.folderName) errors.push('folder_name_required');
  else { const fv = validateFolderName(pj.folderName); if (!fv.ok) errors.push(...fv.errors); }
  if (!Object.values(PROJECT_STATE).includes(pj.state)) errors.push('state_invalid');

  if (pj.subscriptionEnabled) {
    const price = Number(pj.subscriptionPrice);
    if (!Number.isFinite(price) || price < 0) errors.push('subscription_price_negative');
  }
  const dur = Number(pj.subscriptionDurationDays);
  if (pj.subscriptionDurationDays != null && (!Number.isFinite(dur) || dur < 1 || dur > 3650)) {
    errors.push('subscription_duration_out_of_range');
  }
  const grace = Number(pj.subscriptionGraceDays);
  if (pj.subscriptionGraceDays != null && (!Number.isFinite(grace) || grace < 0 || grace > 30)) {
    errors.push('subscription_grace_out_of_range');
  }

  return { ok: errors.length === 0, errors };
}

// 🆕 validator برای ویدیوی مطالعه آزاد
export function validateFreeStudyVideo(v) {
  const errors = [];
  if (!v) return { ok: false, errors: ['video_required'] };
  if (!v._id) errors.push('id_required');
  if (!v.title || !String(v.title).trim()) errors.push('title_required');
  if (String(v.title || '').length > 200) errors.push('title_too_long');
  if (v.description && String(v.description).length > 2000) errors.push('description_too_long');
  if (v.durationMinutes != null) {
    const d = Number(v.durationMinutes);
    if (!Number.isFinite(d) || d < 0 || d > 600) errors.push('duration_out_of_range');
  }
  if (v.price != null) {
    const p = Number(v.price);
    if (!Number.isFinite(p) || p < 0) errors.push('price_invalid');
  }
  if (!v.blobId && !v.externalUrl) errors.push('source_required');
  if (!Number.isFinite(v.order)) errors.push('order_invalid');
  return { ok: errors.length === 0, errors };
}

export function validateProjectVersion(v) {
  const errors = [];
  if (!v) return { ok: false, errors: ['version_required'] };
  if (!v.projectId) errors.push('project_id_required');
  if (!Object.values(VERSION_STATUS).includes(v.status)) errors.push('status_invalid');

  // 🆕 تشخیص نوع از پروژه
  let isFreeStudy = false;
  try {
    const project = dataEngine.project.get(v.projectId);
    isFreeStudy = project?.type === 'freeStudy';
  } catch {}

  if (isFreeStudy) {
    if (!Array.isArray(v.videos)) errors.push('videos_not_array');
    else v.videos.forEach((vid, i) => {
      const vv = validateFreeStudyVideo(vid);
      if (!vv.ok) errors.push(...vv.errors.map((e) => `video[${i}].${e}`));
    });
  } else {
    if (!Array.isArray(v.chapters)) errors.push('chapters_not_array');
    else v.chapters.forEach((ch, i) => {
      const cv = validateChapter(ch);
      if (!cv.ok) errors.push(...cv.errors.map((e) => `chapter[${i}].${e}`));
    });
  }

  return { ok: errors.length === 0, errors };
}

export function validateChapter(ch) {
  const errors = [];
  if (!ch) return { ok: false, errors: ['chapter_required'] };
  if (!ch._id) errors.push('id_required');
  if (!ch.title || !String(ch.title).trim()) errors.push('title_required');
  if (String(ch.title).length > 200) errors.push('title_too_long');
  if (!Number.isFinite(ch.order)) errors.push('order_invalid');
  if (!Array.isArray(ch.lessons)) errors.push('lessons_not_array');
  else ch.lessons.forEach((ls, i) => {
    const lv = validateLesson(ls);
    if (!lv.ok) errors.push(...lv.errors.map((e) => `lesson[${i}].${e}`));
  });
  return { ok: errors.length === 0, errors };
}

export function validateLesson(ls) {
  const errors = [];
  if (!ls) return { ok: false, errors: ['lesson_required'] };
  if (!ls._id) errors.push('id_required');
  if (!ls.title || !String(ls.title).trim()) errors.push('title_required');
  if (String(ls.title).length > 200) errors.push('title_too_long');
  if (ls.description && String(ls.description).length > 5000) errors.push('description_too_long');
  if (!Number.isFinite(ls.order)) errors.push('order_invalid');
  if (!Array.isArray(ls.steps)) errors.push('steps_not_array');
  else ls.steps.forEach((st, i) => {
    const sv = validateStep(st);
    if (!sv.ok) errors.push(...sv.errors.map((e) => `step[${i}].${e}`));
  });
  if (ls.isFree != null && typeof ls.isFree !== 'boolean') 
    errors.push('is_free_invalid');
  return { ok: errors.length === 0, errors };
}

export function validateStep(st) {
  const errors = [];
  if (!st) return { ok: false, errors: ['step_required'] };
  if (!st._id) errors.push('id_required');
  if (!st.title || !String(st.title).trim()) errors.push('title_required');
  if (String(st.title).length > 200) errors.push('title_too_long');
  if (st.description && String(st.description).length > 5000) errors.push('description_too_long');
  if (st.durationMinutes != null && (st.durationMinutes < 0 || st.durationMinutes > 1440)) {
    errors.push('duration_out_of_range');
  }
  if (!Number.isFinite(st.order)) errors.push('order_invalid');
  if (!Array.isArray(st.videos)) errors.push('videos_not_array');
  else st.videos.forEach((v, i) => {
    const vv = validateVideoRef(v);
    if (!vv.ok) errors.push(...vv.errors.map((e) => `video[${i}].${e}`));
  });

  if (st.type && !Object.values(STEP_TYPE).includes(st.type)) {
    errors.push('step_type_invalid');
  }
  if (st.type === STEP_TYPE.QUIZ) {
    if (st.quizId != null && typeof st.quizId !== 'string') {
      errors.push('quiz_id_invalid');
    }
    if (st.quizMeta) {
      const pt = Number(st.quizMeta.passThreshold);
      if (Number.isFinite(pt) && (pt < 0 || pt > 20)) {
        errors.push('pass_threshold_out_of_range');
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

export function validateVideoRef(v) {
  const errors = [];
  if (!v) return { ok: false, errors: ['video_required'] };
  if (!v._id) errors.push('id_required');
  if (!v.blobId && !v.externalUrl) errors.push('source_required');
  if (v.externalUrl && !/^https?:\/\//i.test(v.externalUrl)) errors.push('url_invalid');
  return { ok: errors.length === 0, errors };
}

/** ترتیب را دوباره از صفر شماره‌گذاری می‌کند */
export function resequence(list) {
  if (!Array.isArray(list)) return list;
  return list.map((item, i) => ({ ...item, order: i }));
}

// ================================================================
// SECTION 6: NORMALIZERS
// ================================================================

// ================================================================
// 🔽 CHANGED: همه‌ی normalizeها delegate به curriculum می‌شن
// ----------------------------------------------------------------
// این تضمین می‌کنه که: ۱-۱۲ پایه، ۲۹ درس، ۳ رشته پشتیبانی بشن
// ================================================================

export function normalizeGrade(grade) {
  return normalizeGradeId(grade);
}

export function normalizeSubject(subject) {
  return normalizeSubjectId(subject);
}

// 🔽 NEW: تابع جدید برای رشته (فقط ۱۰ تا ۱۲)
export function normalizeField(field) {
  return normalizeFieldId(field);
}

/** ساده‌ساز hash برای استفاده در contentHash */
function deepSort(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(deepSort);
  const out = {};
  for (const k of Object.keys(obj).sort()) out[k] = deepSort(obj[k]);
  return out;
}

// ================================================================
// SECTION 7: BASE REGISTRY
// ================================================================

class BaseRegistry {
  constructor({ key, name, eventPrefix = 'registry' }) {
    this.key = key;
    this.name = name;
    this.eventPrefix = eventPrefix;
  }

  _readAll() {
    const data = engine.get(this.key, {});
    return data && typeof data === 'object' ? data : {};
  }

  _writeAll(data) {
    const res = engine.set(this.key, data);
    if (res.ok) {
      engine.broadcast(`${this.eventPrefix}:changed`, { key: this.key });
      engine.emit(`${this.eventPrefix}:changed`, { key: this.key, name: this.name });
    }
    return res;
  }

  _writeOne(id, entity) {
    const all = this._readAll();
    all[id] = entity;
    return this._writeAll(all);
  }

  _removeOne(id) {
    const all = this._readAll();
    if (!(id in all)) return { ok: true, notFound: true };
    delete all[id];
    return this._writeAll(all);
  }

  _getOne(id) { return this._readAll()[id] || null; }
  _listAll() { return Object.values(this._readAll()); }

  clear() { return this._writeAll({}); }
  count() { return this._listAll().length; }
}

// ================================================================
// SECTION 8: TEACHER REGISTRY
// ================================================================

export class TeacherRegistry extends BaseRegistry {
  constructor() {
    super({ key: KEYS.TEACHERS, name: 'teacher', eventPrefix: 'registry:teacher' });
  }

  _rebuildIndexes() {
    const teachers = this._readAll();
    const byUser = {};
    const byFolder = {};
    for (const tp of Object.values(teachers)) {
      if (tp.userId) byUser[tp.userId] = tp.id;
      if (tp.folderName) byFolder[tp.folderName] = tp.id;
    }
    engine.set(KEYS.TEACHER_BY_USER, byUser, { silent: true });
    engine.set(KEYS.TEACHER_BY_FOLDER, byFolder, { silent: true });
  }

  create(input) {
    const tp = input.id && input.createdAt ? input : createTeacherProfile(input);
    const v = validateTeacherProfile(tp);
    if (!v.ok) return { ok: false, errors: v.errors };
    if (this._getOne(tp.id)) return { ok: false, errors: ['duplicate_id'] };
    if (tp.folderName && this.getByFolderName(tp.folderName)) {
      return { ok: false, errors: ['folder_name_taken'] };
    }
    if (tp.userId && this.getByUserId(tp.userId)) {
      return { ok: false, errors: ['user_already_teacher'] };
    }
    tp.createdAt = tp.createdAt || now();
    tp.updatedAt = now();
    this._writeOne(tp.id, tp);
    this._rebuildIndexes();
    return { ok: true, data: tp };
  }

  get(id) { return this._getOne(id); }

  getByUserId(userId) {
    if (!userId) return null;
    const idx = engine.get(KEYS.TEACHER_BY_USER, {}) || {};
    const tid = idx[userId];
    return tid ? this._getOne(tid) : null;
  }

  getByFolderName(folderName) {
    if (!folderName) return null;
    const idx = engine.get(KEYS.TEACHER_BY_FOLDER, {}) || {};
    const tid = idx[folderName];
    return tid ? this._getOne(tid) : null;
  }

  list({ includeArchived = false } = {}) {
    const all = this._listAll();
    return includeArchived ? all : all.filter((t) => !t.archivedAt);
  }

  listActive() { return this.list().filter((t) => t.isActive); }

  update(id, patch) {
    const tp = this._getOne(id);
    if (!tp) return { ok: false, errors: ['not_found'] };

    // قفل شدن folderName بعد از انتشار
    if (patch.folderName && patch.folderName !== tp.folderName) {
      const projects = dataEngine.project.listByTeacher(id, { includeArchived: true });
      const hasPublished = projects.some((p) => p.publishedVersionId);
      if (hasPublished) return { ok: false, errors: ['folder_name_locked'] };
      const fv = validateFolderName(patch.folderName);
      if (!fv.ok) return { ok: false, errors: fv.errors };
      if (this.getByFolderName(patch.folderName)) {
        return { ok: false, errors: ['folder_name_taken'] };
      }
    }

    // userId غیرقابل‌تغییر بعد از set شدن
    if (patch.userId && patch.userId !== tp.userId) {
      if (tp.userId) return { ok: false, errors: ['user_id_locked'] };
      if (this.getByUserId(patch.userId)) return { ok: false, errors: ['user_already_teacher'] };
    }

    const updated = { ...tp, ...patch, updatedAt: now() };
    updated.id = tp.id;
    updated.createdAt = tp.createdAt;
    updated._v = DATA_SCHEMA;

    const v = validateTeacherProfile(updated);
    if (!v.ok) return { ok: false, errors: v.errors };

    this._writeOne(id, updated);
    this._rebuildIndexes();
    return { ok: true, data: updated };
  }

  softDelete(id) {
    const tp = this._getOne(id);
    if (!tp) return { ok: false, errors: ['not_found'] };
    tp.archivedAt = now();
    tp.isActive = false;
    tp.updatedAt = now();
    this._writeOne(id, tp);
    return { ok: true, data: tp };
  }

  restore(id) {
    const tp = this._getOne(id);
    if (!tp) return { ok: false, errors: ['not_found'] };
    tp.archivedAt = null;
    tp.isActive = true;
    tp.updatedAt = now();
    this._writeOne(id, tp);
    return { ok: true, data: tp };
  }

  hardDelete(id) { return this._removeOne(id); }
}

// ================================================================
// SECTION 9: REQUEST REGISTRY
// ================================================================

export class RequestRegistry extends BaseRegistry {
  constructor() {
    super({ key: KEYS.REQUESTS, name: 'request', eventPrefix: 'registry:request' });
  }

  findByIdempotency(key) {
    if (!key) return null;
    const idx = engine.get(KEYS.REQUEST_IDEMPOTENCY, {}) || {};
    const rid = idx[key];
    return rid ? this._getOne(rid) : null;
  }

  _updateIdempotencyIndex(id, key) {
    if (!key) return;
    const idx = engine.get(KEYS.REQUEST_IDEMPOTENCY, {}) || {};
    idx[key] = id;
    engine.set(KEYS.REQUEST_IDEMPOTENCY, idx, { silent: true });
  }

  _removeIdempotency(id) {
    const idx = engine.get(KEYS.REQUEST_IDEMPOTENCY, {}) || {};
    for (const k of Object.keys(idx)) if (idx[k] === id) delete idx[k];
    engine.set(KEYS.REQUEST_IDEMPOTENCY, idx, { silent: true });
  }

  create(input) {
    const rq = input.id && input.createdAt ? input : createTeacherRequest(input);
    const v = validateTeacherRequest(rq);
    if (!v.ok) return { ok: false, errors: v.errors };
    if (this._getOne(rq.id)) return { ok: false, errors: ['duplicate_id'] };

    // idempotency guard
    if (rq.idempotencyKey) {
      const existing = this.findByIdempotency(rq.idempotencyKey);
      if (existing) return { ok: true, data: existing, idempotent: true };
    }

    // جلوگیری از درخواست تکراری pending برای همون (teacher, grade, subject)
        // جلوگیری از درخواست تکراری pending برای همون (teacher, grade, field, subject)
    const pendingSame = this.list().find(
      (r) =>
        r.teacherId === rq.teacherId &&
        String(r.grade) === String(rq.grade) &&
        (r.field || null) === (rq.field || null) &&        // 🔽 CHANGED
        r.subject === rq.subject &&
        [REQUEST_STATUS.PENDING, REQUEST_STATUS.EDIT_REQUESTED].includes(r.status)
    );
    if (pendingSame) {
      return { ok: false, errors: ['duplicate_pending_request'], existingId: pendingSame.id };
    }

    rq.createdAt = rq.createdAt || now();
    rq.updatedAt = now();

    if (rq.history.length === 0) {
      rq.history.push({
        fromStatus: null,
        toStatus: rq.status,
        at: rq.createdAt,
        byUserId: rq.userId || rq.teacherId || null,
        note: 'created',
      });
    }

    this._writeOne(rq.id, rq);
    this._updateIdempotencyIndex(rq.id, rq.idempotencyKey);
    return { ok: true, data: rq };
  }

  get(id) { return this._getOne(id); }

    list(filter = {}) {
    let list = this._listAll();
    if (filter.status) {
      const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
      list = list.filter((r) => statuses.includes(r.status));
    }
    if (filter.teacherId) list = list.filter((r) => r.teacherId === filter.teacherId);
    if (filter.userId) list = list.filter((r) => r.userId === filter.userId);
    if (filter.grade) list = list.filter((r) => String(r.grade) === String(filter.grade));
    // 🔽 CHANGED: فیلتر field
    if (filter.field !== undefined) {
      const f = filter.field ? normalizeFieldId(filter.field) : null;
      list = list.filter((r) => (r.field || null) === f);
    }
    if (filter.subject) list = list.filter((r) => r.subject === filter.subject);
    if (!filter.includeArchived) list = list.filter((r) => !r.archivedAt);
    return list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  listPending()       { return this.list({ status: [REQUEST_STATUS.PENDING] }); }
  listEditRequested() { return this.list({ status: [REQUEST_STATUS.EDIT_REQUESTED] }); }
  listByTeacher(id)   { return this.list({ teacherId: id }); }
  listByUser(id)      { return this.list({ userId: id }); }

  _transition(id, toStatus, { byUserId, note = '' } = {}) {
    const rq = this._getOne(id);
    if (!rq) return { ok: false, errors: ['not_found'] };
    const allowed = REQUEST_TRANSITIONS[rq.status] || [];
    if (!allowed.includes(toStatus)) {
      return { ok: false, errors: [`invalid_transition_${rq.status}_to_${toStatus}`] };
    }
    const fromStatus = rq.status;
    rq.status = toStatus;
    rq.updatedAt = now();
    rq.history.push({
      fromStatus,
      toStatus,
      at: now(),
      byUserId: byUserId || null,
      note: note || '',
    });
    if (toStatus === REQUEST_STATUS.PENDING) rq.submittedAt = now();
    if (toStatus === REQUEST_STATUS.WITHDRAWN) rq.withdrawnAt = now();
    if ([REQUEST_STATUS.APPROVED, REQUEST_STATUS.REJECTED, REQUEST_STATUS.EDIT_REQUESTED].includes(toStatus)) {
      rq.reviewedAt = now();
    }
    this._writeOne(id, rq);
    return { ok: true, data: rq };
  }

  submit(id, { byUserId } = {}) {
    return this._transition(id, REQUEST_STATUS.PENDING, { byUserId, note: 'submitted' });
  }

  withdraw(id, { byUserId, note = 'withdrawn' } = {}) {
    return this._transition(id, REQUEST_STATUS.WITHDRAWN, { byUserId, note });
  }

  approve(id, { adminId, note = '' } = {}) {
    const r = this._transition(id, REQUEST_STATUS.APPROVED, { byUserId: adminId, note });
    if (r.ok) {
      r.data.adminId = adminId || null;
      r.data.adminNotes = note || '';
      this._writeOne(id, r.data);
    }
    return r;
  }

  reject(id, { adminId, note = '' } = {}) {
    const r = this._transition(id, REQUEST_STATUS.REJECTED, { byUserId: adminId, note });
    if (r.ok) {
      r.data.adminId = adminId || null;
      r.data.adminNotes = note || '';
      this._writeOne(id, r.data);
    }
    return r;
  }

  requestEdit(id, { adminId, note = '' } = {}) {
    const r = this._transition(id, REQUEST_STATUS.EDIT_REQUESTED, { byUserId: adminId, note });
    if (r.ok) {
      r.data.adminId = adminId || null;
      r.data.adminNotes = note || '';
      this._writeOne(id, r.data);
    }
    return r;
  }

  updateFormData(id, patch) {
    const rq = this._getOne(id);
    if (!rq) return { ok: false, errors: ['not_found'] };
    if (![REQUEST_STATUS.DRAFT, REQUEST_STATUS.EDIT_REQUESTED].includes(rq.status)) {
      return { ok: false, errors: ['cannot_edit_in_status'] };
    }
    rq.formData = { ...rq.formData, ...patch };
    rq.updatedAt = now();
    this._writeOne(id, rq);
    return { ok: true, data: rq };
  }

    updateProposed(id, { grade, field, subject, proposedFolderName } = {}) {
    const rq = this._getOne(id);
    if (!rq) return { ok: false, errors: ['not_found'] };
    if (![REQUEST_STATUS.DRAFT, REQUEST_STATUS.EDIT_REQUESTED].includes(rq.status)) {
      return { ok: false, errors: ['cannot_edit_in_status'] };
    }
    if (grade != null) {
      const g = normalizeGrade(grade);
      if (!g) return { ok: false, errors: ['grade_invalid'] };
      rq.grade = g;
    }
    // 🔽 CHANGED: پذیرش field
    if (field !== undefined) {
      const f = field ? normalizeField(field) : null;
      if (field && !f) return { ok: false, errors: ['field_invalid'] };
      rq.field = f;
    }
    // 🔽 CHANGED: اعتبارسنجی تطابق پایه/رشته بعد از تغییر
    const gradeNeedsField = rq.grade && needsField(rq.grade);
    if (gradeNeedsField && !rq.field) {
      return { ok: false, errors: ['field_required'] };
    }
    if (!gradeNeedsField && rq.field) {
      return { ok: false, errors: ['field_not_allowed'] };
    }
    if (subject != null) {
      const s = normalizeSubject(subject);
      if (!s) return { ok: false, errors: ['subject_invalid'] };
      rq.subject = s;
    }
    if (proposedFolderName != null) {
      const v = validateFolderName(proposedFolderName);
      if (!v.ok) return { ok: false, errors: v.errors };
      if (dataEngine.teacher.getByFolderName(proposedFolderName)) {
        return { ok: false, errors: ['folder_name_taken'] };
      }
      rq.proposedFolderName = proposedFolderName;
    }
    rq.updatedAt = now();
    this._writeOne(id, rq);
    return { ok: true, data: rq };
  }

  linkProject(id, projectId) {
    const rq = this._getOne(id);
    if (!rq) return { ok: false, errors: ['not_found'] };
    rq.relatedProjectId = projectId;
    rq.updatedAt = now();
    this._writeOne(id, rq);
    return { ok: true, data: rq };
  }
}

// ================================================================
// SECTION 10: PROJECT REGISTRY
// ================================================================

export class ProjectRegistry extends BaseRegistry {
  constructor() {
    super({ key: KEYS.PROJECTS, name: 'project', eventPrefix: 'registry:project' });
  }

    _rebuildIndexes() {
    const all = this._readAll();
    const byTeacher = {};
    const byKey = {};
    for (const pj of Object.values(all)) {
      if (pj.archivedAt) continue;
      if (pj.teacherId) {
        if (!byTeacher[pj.teacherId]) byTeacher[pj.teacherId] = [];
        byTeacher[pj.teacherId].push(pj.id);
      }
      // 🆕 فقط کتاب‌ها در byKey
      if (pj.type !== 'freeStudy' && pj.grade && pj.subject && pj.folderName) {
        const key = pj.field
          ? `${pj.grade}-${pj.field}-${pj.subject}-${pj.folderName}`
          : `${pj.grade}-${pj.subject}-${pj.folderName}`;
        byKey[key] = pj.id;
      }
    }
    engine.set(KEYS.PROJECT_BY_TEACHER, byTeacher, { silent: true });
    engine.set(KEYS.PROJECT_BY_KEY, byKey, { silent: true });
  }

    create(input) {
    const pj = input.id && input.createdAt ? input : createProject(input);
    const v = validateProject(pj);
    if (!v.ok) return { ok: false, errors: v.errors };
    if (this._getOne(pj.id)) return { ok: false, errors: ['duplicate_id'] };

    // 🆕 فقط کتاب‌ها key collision دارند
    if (pj.type !== 'freeStudy') {
      const existing = this.getByKey(pj.grade, pj.subject, pj.folderName, pj.field || null);
      if (existing) return { ok: false, errors: ['project_key_taken'], existingId: existing.id };
    }

    if (!dataEngine.teacher.get(pj.teacherId)) {
      return { ok: false, errors: ['teacher_not_found'] };
    }

    pj.createdAt = pj.createdAt || now();
    pj.updatedAt = now();
    this._writeOne(pj.id, pj);
    this._rebuildIndexes();
    return { ok: true, data: pj };
  }

  get(id) { return this._getOne(id); }

    getByKey(grade, subject, folderName, field = null) {
    if (!grade || !subject || !folderName) return null;
    const idx = engine.get(KEYS.PROJECT_BY_KEY, {}) || {};
    // 🔽 CHANGED: با field کلید جدید، بدون field کلید قدیمی (backward compat)
    const f = field ? normalizeFieldId(field) : null;
    const key = f
      ? `${grade}-${f}-${subject}-${folderName}`
      : `${grade}-${subject}-${folderName}`;
    const pid = idx[key];
    return pid ? this._getOne(pid) : null;
  }

  listByTeacher(teacherId, { includeArchived = false, type = 'book' } = {}) {
    const idx = engine.get(KEYS.PROJECT_BY_TEACHER, {}) || {};
    const ids = idx[teacherId] || [];
    const all = ids.map((id) => this._getOne(id)).filter(Boolean);
    const filtered = includeArchived ? all : all.filter((p) => !p.archivedAt);
    if (type === 'all') return filtered;
    return filtered.filter((p) => (p.type || 'book') === type);
  }

    list(filter = {}) {
    let list = this._listAll();
    // 🆕 فیلتر نوع (پیش‌فرض بدون فیلتر = همه)
    if (filter.type === 'freeStudy') {
      list = list.filter((p) => p.type === 'freeStudy');
    } else if (filter.type === 'book') {
      list = list.filter((p) => !p.type || p.type === 'book');
    } else if (filter.type === 'all') {
      // بدون فیلتر
    }
    if (filter.state) {
      const states = Array.isArray(filter.state) ? filter.state : [filter.state];
      list = list.filter((p) => states.includes(p.state));
    }
    if (filter.grade) list = list.filter((p) => String(p.grade) === String(filter.grade));
    if (filter.field !== undefined) {
      const f = filter.field ? normalizeFieldId(filter.field) : null;
      list = list.filter((p) => (p.field || null) === f);
    }
    if (filter.subject) list = list.filter((p) => p.subject === filter.subject);
    if (filter.teacherId) list = list.filter((p) => p.teacherId === filter.teacherId);
    if (filter.folderName) list = list.filter((p) => p.folderName === filter.folderName);
    if (filter.category) list = list.filter((p) => p.category === filter.category); // 🆕
    if (!filter.includeArchived) list = list.filter((p) => !p.archivedAt);
    return list;
  }

  listPublished() { return this.list({ state: PROJECT_STATE.PUBLISHED }); }

    listPublishedBySubject(grade, subject, field = null) {
    const filter = { grade, subject, state: PROJECT_STATE.PUBLISHED };
    // 🔽 CHANGED: فیلتر field
    if (field) {
      const f = normalizeFieldId(field);
      if (f) filter.field = f;
    } else {
      // وقتی field نده، فقط پروژه‌های 1-9 (بدون field) رو برگردون
      filter.field = null;
    }
    return this.list(filter);
  }

  update(id, patch) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };

    // قفل شدن کلیدهای هویتی بعد از انتشار
        // قفل شدن کلیدهای هویتی بعد از انتشار
    if (patch.folderName && patch.folderName !== pj.folderName) {
      if (pj.publishedVersionId) return { ok: false, errors: ['folder_name_locked'] };
      const fv = validateFolderName(patch.folderName);
      if (!fv.ok) return { ok: false, errors: fv.errors };
      // 🔽 CHANGED: پاس‌دادن field
      const conflict = this.getByKey(pj.grade, pj.subject, patch.folderName, pj.field || null);
      if (conflict && conflict.id !== id) return { ok: false, errors: ['folder_name_taken'] };
    }
    if (patch.grade != null && String(patch.grade) !== String(pj.grade)) {
      if (pj.publishedVersionId) return { ok: false, errors: ['grade_locked'] };
    }
    // 🔽 CHANGED: قفل field بعد از انتشار
    if (patch.field !== undefined && (patch.field || null) !== (pj.field || null)) {
      if (pj.publishedVersionId) return { ok: false, errors: ['field_locked'] };
      const f = patch.field ? normalizeFieldId(patch.field) : null;
      if (patch.field && !f) return { ok: false, errors: ['field_invalid'] };
      patch.field = f;
    }
    if (patch.subject && patch.subject !== pj.subject) {
      if (pj.publishedVersionId) return { ok: false, errors: ['subject_locked'] };
    }

    const updated = { ...pj, ...patch, updatedAt: now() };
    updated.id = pj.id;
    updated.createdAt = pj.createdAt;
    updated._v = DATA_SCHEMA;

    const v = validateProject(updated);
    if (!v.ok) return { ok: false, errors: v.errors };

    this._writeOne(id, updated);
    this._rebuildIndexes();
    return { ok: true, data: updated };
  }





    // 🆕 آپدیت گروهی — یک write، یک rebuild، یک push به سرور
  batchUpdate(mutator) {
    if (typeof mutator !== 'function') {
      return { ok: false, errors: ['invalid_mutator'] };
    }
    const all = this._readAll();
    const now = Date.now();
    let changed = 0;

    for (const id of Object.keys(all)) {
      const cur = all[id];
      if (!cur) continue;
      let patched;
      try { patched = mutator(cur); } catch { continue; }
      if (patched && typeof patched === 'object') {
        all[id] = { ...cur, ...patched, updatedAt: now };
        changed++;
      }
    }

    if (changed > 0) {
      this._writeAll(all);
      this._rebuildIndexes();
    }
    return { ok: true, count: changed };
  }





  transitionState(id, toState, { byUserId } = {}) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    const allowed = PROJECT_TRANSITIONS[pj.state] || [];
    if (!allowed.includes(toState)) {
      return { ok: false, errors: [`invalid_transition_${pj.state}_to_${toState}`] };
    }
    pj.state = toState;
    pj.updatedAt = now();
    if (toState === PROJECT_STATE.PUBLISHED && !pj.publishedAt) pj.publishedAt = now();
    if (toState === PROJECT_STATE.SUSPENDED) pj.suspendedAt = now();
    this._writeOne(id, pj);
    return { ok: true, data: pj };
  }

  setDraft(id, versionId)  { return this.update(id, { draftVersionId: versionId }); }

  setPending(id, versionId) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    pj.pendingVersionId = versionId;
    pj.updatedAt = now();
    this._writeOne(id, pj);
    return { ok: true, data: pj };
  }

  clearPending(id) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    pj.pendingVersionId = null;
    pj.updatedAt = now();
    this._writeOne(id, pj);
    return { ok: true, data: pj };
  }

  clearDraft(id) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    pj.draftVersionId = null;
    pj.updatedAt = now();
    this._writeOne(id, pj);
    return { ok: true, data: pj };
  }

  /**
   * چرخش نسخه‌ی منتشرشده:
   *   oldPublished → superseded
   *   newVersion → published
   *   draft و pending ریست می‌شن
   */
  publish(id, newVersionId, { byUserId } = {}) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    const newVer = dataEngine.version.get(newVersionId);
    if (!newVer) return { ok: false, errors: ['version_not_found'] };
    if (newVer.projectId !== id) return { ok: false, errors: ['version_project_mismatch'] };

    const oldPublishedId = pj.publishedVersionId;

    pj.publishedVersionId = newVersionId;
    pj.pendingVersionId = null;
    pj.draftVersionId = null;
    pj.state = PROJECT_STATE.PUBLISHED;
    if (!pj.publishedAt) pj.publishedAt = now();
    pj.updatedAt = now();
    if (!pj.versions.includes(newVersionId)) pj.versions.push(newVersionId);

    this._writeOne(id, pj);

    dataEngine.version.markPublished(newVersionId, { byUserId });
    if (oldPublishedId && oldPublishedId !== newVersionId) {
      dataEngine.version.markSuperseded(oldPublishedId);
    }
    return { ok: true, data: pj, oldPublishedId };
  }

  softDelete(id) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    pj.archivedAt = now();
    pj.updatedAt = now();
    this._writeOne(id, pj);
    this._rebuildIndexes();
    return { ok: true, data: pj };
  }

  restore(id) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };
    pj.archivedAt = null;
    pj.updatedAt = now();
    this._writeOne(id, pj);
    this._rebuildIndexes();
    return { ok: true, data: pj };
  }



    /** 
   * حذف کامل پروژه + همه‌ی نسخه‌ها + آرشیو پیشرفت دانش‌آموزان
   * @returns { ok, data, blobIds, versionIds }
   */
  hardDelete(id) {
    const pj = this._getOne(id);
    if (!pj) return { ok: false, errors: ['not_found'] };

    // ۱. جمع‌آوری blob IDs از cover + همه‌ی lessons و videos
    const blobIds = new Set();
    if (pj.coverImageBlobId) blobIds.add(pj.coverImageBlobId);
    if (pj.image) blobIds.add(pj.image); // 🆕

    const versionIds = Array.isArray(pj.versions) ? [...pj.versions] : [];

    for (const vid of versionIds) {
      const ver = dataEngine.version.get(vid);
      if (!ver) continue;

      // 🆕 ویدیوهای مطالعه آزاد
      for (const v of ver.videos || []) {
        if (v.blobId) blobIds.add(v.blobId);
        if (v.thumbnailBlobId) blobIds.add(v.thumbnailBlobId);
      }

      // کتاب
      for (const ch of ver.chapters || []) {
        for (const ls of ch.lessons || []) {
          if (ls.imageBlobId) blobIds.add(ls.imageBlobId);
          for (const st of ls.steps || []) {
            for (const v of st.videos || []) {
              if (v.blobId) blobIds.add(v.blobId);
            }
          }
        }
      }
      // حذف نسخه
      dataEngine.version.hardDelete(vid);
    }

    // ۲. حذف پروژه از registry
    this._removeOne(id);
    this._rebuildIndexes();

    // ۳. 🆕 اعلام حذف به remoteSync — تا از سرور هم حذف شود
    // (emit بعد از پاک‌سازی محلی، تا اگر listener خطا داد، state داخلی سالم بماند)
    try {
      for (const vid of versionIds) {
        engine.emit('entity:deleted', { col: 'versions', id: vid });
      }
      engine.emit('entity:deleted', { col: 'projects', id });
    } catch (e) {
      console.warn('[dataEngine.project.hardDelete] emit failed:', e.message);
    }

    return {
      ok: true,
      data: pj,
      blobIds: Array.from(blobIds),
      versionIds,
    };
  }
}

// ================================================================
// SECTION 11: VERSION REGISTRY
// ================================================================

export class VersionRegistry extends BaseRegistry {
  constructor() {
    super({ key: KEYS.VERSIONS, name: 'version', eventPrefix: 'registry:version' });
  }

  create(input) {
    const v = input.id && input.createdAt ? input : createProjectVersion(input);
    const val = validateProjectVersion(v);
    if (!val.ok) return { ok: false, errors: val.errors };
    if (this._getOne(v.id)) return { ok: false, errors: ['duplicate_id'] };
    if (!dataEngine.project.get(v.projectId)) {
      return { ok: false, errors: ['project_not_found'] };
    }

    v.contentHash = v.contentHash || this.contentHash(v);
    v.createdAt = v.createdAt || now();
    this._writeOne(v.id, v);



    // ثبت در لیست نسخه‌های پروژه
    const pj = dataEngine.project.get(v.projectId);
    if (pj && !pj.versions.includes(v.id)) {
      pj.versions.push(v.id);
      dataEngine.project.update(v.projectId, { versions: pj.versions });
    }
    return { ok: true, data: v };
  }


    /** حذف فیزیکی نسخه */
  hardDelete(id) {
    return this._removeOne(id);
  }



  get(id) { return this._getOne(id); }

  listByProject(projectId, filter = {}) {
    let list = this._listAll().filter((v) => v.projectId === projectId);
    if (filter.status) {
      const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
      list = list.filter((v) => statuses.includes(v.status));
    }
    return list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }

  getLatestByProject(projectId, status) {
    return this.listByProject(projectId, { status })[0] || null;
  }









    /** ✅ مهاجرت: تخصیص legacyLessonId پایدار به درس‌های قدیمی */
  migrateLegacyLessonIds(projectId) {
    const project = dataEngine.project.get(projectId);
    if (!project) return { ok: false, migrated: 0 };

    const versionIds = [
      project.draftVersionId,
      project.pendingVersionId,
      project.publishedVersionId,
    ].filter(Boolean);

    let migrated = 0;

    for (const vid of versionIds) {
      const ver = dataEngine.version.get(vid);
      if (!ver) continue;

      // جمع‌آوری شماره‌های موجود
      let counter = 0;
      for (const ch of ver.chapters || []) {
        for (const ls of ch.lessons || []) {
          const m = String(ls.legacyLessonId || '').match(/^Lesson(\d+)$/);
          if (m) counter = Math.max(counter, parseInt(m[1], 10));
        }
      }

      // تخصیص شماره به درس‌های بدون legacyLessonId
      let changed = false;
      const updated = (ver.chapters || []).map((ch) => ({
        ...ch,
        lessons: (ch.lessons || []).map((ls) => {
          if (ls.legacyLessonId) return ls;
          counter += 1;
          migrated += 1;
          changed = true;
          return { ...ls, legacyLessonId: `Lesson${counter}` };
        }),
      }));

      if (changed) {
        this.updateChapters(vid, updated);
      }
    }

    return { ok: true, migrated };
  }













  update(id, patch) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    if (v.status === VERSION_STATUS.PUBLISHED) {
      return { ok: false, errors: ['cannot_edit_published_version'] };
    }
    const updated = {
      ...v, ...patch,
      id: v.id,
      createdAt: v.createdAt,
      updatedAt: now(),   // 🆕
      _v: DATA_SCHEMA,
    };
    if (patch.chapters) updated.contentHash = this.contentHash(updated);
    const val = validateProjectVersion(updated);
    if (!val.ok) return { ok: false, errors: val.errors };
    this._writeOne(id, updated);
    return { ok: true, data: updated };
  }

  updateChapters(id, chapters) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    if (v.status === VERSION_STATUS.PUBLISHED) {
      return { ok: false, errors: ['cannot_edit_published_version'] };
    }
    v.chapters = chapters.map(cloneChapter);
    v.contentHash = this.contentHash(v);
    v.updatedAt = now();   // 🆕 ← این خط مشکل را حل می‌کند
    this._writeOne(id, v);
    return { ok: true, data: v };
  }







    // 🆕 به‌روزرسانی ویدیوهای یک نسخه‌ی مطالعه آزاد
  updateVideos(id, videos) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    if (v.status === VERSION_STATUS.PUBLISHED) {
      return { ok: false, errors: ['cannot_edit_published_version'] };
    }
    v.videos = (Array.isArray(videos) ? videos : []).map(cloneFreeStudyVideo);
    v.contentHash = this.contentHash(v);
    v.updatedAt = now();
    this._writeOne(id, v);
    return { ok: true, data: v };
  }







  markPublished(id, { byUserId } = {}) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    v.status = VERSION_STATUS.PUBLISHED;
    v.publishedAt = now();
    v.publishedBy = byUserId || null;
    v.updatedAt = now();   // 🆕
    this._writeOne(id, v);
    return { ok: true, data: v };
  }

  markSuperseded(id) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    v.status = VERSION_STATUS.SUPERSEDED;
    v.updatedAt = now();   // 🆕
    this._writeOne(id, v);
    return { ok: true, data: v };
  }

  markPending(id) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    v.status = VERSION_STATUS.PENDING;
    v.updatedAt = now();   // 🆕
    this._writeOne(id, v);
    return { ok: true, data: v };
  }

  markDraft(id) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    v.status = VERSION_STATUS.DRAFT;
    v.updatedAt = now();   // 🆕
    this._writeOne(id, v);
    return { ok: true, data: v };
  }

  markRejected(id, { reason = '', byUserId } = {}) {
    const v = this._getOne(id);
    if (!v) return { ok: false, errors: ['not_found'] };
    v.status = VERSION_STATUS.REJECTED;
    v.rejectionReason = reason;
    v.reviewedAt = now();
    v.reviewedBy = byUserId || null;
    v.updatedAt = now();   // 🆕
    this._writeOne(id, v);
    return { ok: true, data: v };
  }



















  /** هش محتوای نسخه برای dedup (بدون فیلدهای ولایتایل) */
  contentHash(version) {
    const clone = JSON.parse(JSON.stringify(version));
    const strip = [
      'id', 'projectId', 'parentVersionId', 'status',
      'createdAt', 'createdBy', 'publishedAt', 'publishedBy',
      'reviewedAt', 'reviewedBy', 'rejectionReason', 'adminNotes',
      'contentHash', '_v',
    ];
    for (const k of strip) delete clone[k];
    const sorted = deepSort(clone);
    const str = JSON.stringify(sorted);
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    }
    return `h_${(h >>> 0).toString(16)}_${str.length}`;
  }
}








// ================================================================
// 🆕 SECTION 11.5: PROJECT CATEGORY REGISTRY
// ================================================================

export class ProjectCategoryRegistry extends BaseRegistry {
  constructor() {
    super({
      key: KEYS.PROJECT_CATEGORIES,
      name: 'category',
      eventPrefix: 'registry:category',
    });
  }

  create({ name, sortIndex = null } = {}) {
    const n = String(name || '').trim();
    if (!n) return { ok: false, errors: ['name_required'] };
    if (n.length > 100) return { ok: false, errors: ['name_too_long'] };

    const all = this._listAll();
    if (all.some((c) => c.name === n)) {
      return { ok: false, errors: ['duplicate_name'] };
    }

    const id = `cat_${utils.shortId()}`;
    // 🆕 max+1 → هرگز تصادم نمی‌کنه حتی بعد از حذف
    const nextSort = Number.isFinite(sortIndex)
      ? sortIndex
      : all.reduce((max, c) => Math.max(max, Number(c.sortIndex) || 0), -1) + 1;

    const entity = {
      id,
      name: n,
      sortIndex: nextSort,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    this._writeOne(id, entity);
    return { ok: true, data: entity };
  }

  update(id, patch = {}) {
    const cur = this._getOne(id);
    if (!cur) return { ok: false, errors: ['not_found'] };

    if (patch.name !== undefined) {
      const n = String(patch.name || '').trim();
      if (!n) return { ok: false, errors: ['name_required'] };
      if (n.length > 100) return { ok: false, errors: ['name_too_long'] };
      const dup = this._listAll().find((c) => c.name === n && c.id !== id);
      if (dup) return { ok: false, errors: ['duplicate_name'] };
      patch = { ...patch, name: n };
    }

    const updated = {
      ...cur,
      ...patch,
      id: cur.id,
      createdAt: cur.createdAt,
      updatedAt: Date.now(),
    };

    this._writeOne(id, updated);
    return { ok: true, data: updated };
  }

  remove(id) {
    return this._removeOne(id);
  }

  get(id) {
    return this._getOne(id);
  }

  list() {
    return this._listAll().sort((a, b) => {
      const sA = Number(a.sortIndex) || 0;
      const sB = Number(b.sortIndex) || 0;
      if (sA !== sB) return sA - sB;
      return (a.createdAt || 0) - (b.createdAt || 0);
    });
  }

  findByName(name) {
    const n = String(name || '').trim();
    if (!n) return null;
    return this._listAll().find((c) => c.name === n) || null;
  }

  reorder(orderedIds = []) {
    if (!Array.isArray(orderedIds)) return { ok: false, errors: ['invalid_ids'] };
    const all = this._readAll();
    const now = Date.now();
    orderedIds.forEach((cid, idx) => {
      if (all[cid]) {
        all[cid] = { ...all[cid], sortIndex: idx, updatedAt: now };
      }
    });
    this._writeAll(all);
    return { ok: true };
  }
}







// ================================================================
// SECTION 12: QUERY HELPERS
// ================================================================

export class Query {
  /**
   * لیست معلم‌های منتشرشده برای یک (grade, subject)
   * خروجی: آرایه‌ای برای نمایش در CourseVideos
   */
    getTeachersForSubject(grade, subject, field = null) {
    const g = normalizeGrade(grade);
    const s = normalizeSubject(subject);
    const f = field ? normalizeField(field) : null;
    if (!g || !s) return [];

    // 🔽 CHANGED: پاس‌دادن field
    const projects = dataEngine.project.listPublishedBySubject(g, s, f);
    const result = [];

    for (const pj of projects) {
      const tp = dataEngine.teacher.get(pj.teacherId);
      if (!tp || !tp.isActive || tp.archivedAt) continue;
      const publishedVersion = pj.publishedVersionId
        ? dataEngine.version.get(pj.publishedVersionId)
        : null;

      result.push({
        id: tp.id,
        projectId: pj.id,
        teacherId: tp.id,
        name: tp.fullName,
        folderName: tp.folderName,
        // مسیر عکس: یا legacyAvatar یا avatarBlobId
        image: tp.legacyAvatar || null,
        avatarBlobId: tp.avatarBlobId || null,
        stars: pj.stats?.avgRating || tp.legacyStars || 0,
        studentsRated: pj.stats?.ratingsCount || tp.legacyStudentsRated || 0,
        description: tp.bio ? String(tp.bio).slice(0, 120) : '',
        fullDescription: tp.bio || '',
        isSpecial: !!(pj.stats?.isSpecial || tp.legacyIsSpecial),
        isLegacy: !!pj.isLegacy,
        publishedVersionId: pj.publishedVersionId,
        chapterCount: publishedVersion?.chapters?.length || 0,
        state: pj.state,
      });
    }

    // مرتب‌سازی: ویژه‌ها اول، بعد امتیاز
    return result.sort((a, b) => {
      if (a.isSpecial !== b.isSpecial) return a.isSpecial ? -1 : 1;
      return (b.stars || 0) - (a.stars || 0);
    });
  }

  /**
   * داده‌ی کتاب برای BookChapters
   * @returns {{ projectId, title, subtitle, description, chapters, legacySource, ... } | null}
   */
    getBookData(grade, subject, folderName, field = null) {
    const g = normalizeGrade(grade);
    const s = normalizeSubject(subject);
    const f = field ? normalizeField(field) : null;
    if (!g || !s || !folderName) return null;

    // 🔽 CHANGED: پاس‌دادن field به getByKey
    const pj = dataEngine.project.getByKey(g, s, folderName, f);
    if (!pj) return null;

    const tp = dataEngine.teacher.get(pj.teacherId);
    const ver = pj.publishedVersionId ? dataEngine.version.get(pj.publishedVersionId) : null;

    // 🔽 CHANGED: عنوان با شماره کتاب + رشته
    // برای 1-9: «ریاضی — پایه چهارم»
    // برای 10-12: «فارسی (۱) — پایه دهم (ریاضی)»
    const realField = pj.field || f || null;
    const bookLabel = getBookTitle(g, realField, s)
      || `${SUBJECT_LABELS[s]} - ${GRADE_TITLES[g]}`;

    return {
      projectId: pj.id,
      teacherId: pj.teacherId,
      grade: g,
      field: realField,
      subject: s,
      title: pj.title || bookLabel,
      subtitle: bookLabel,
      description: pj.description || '',
      chapters: ver?.chapters || [],
      legacySource: pj.legacySource || null,
      coverImageBlobId: pj.coverImageBlobId || null,
      publishedVersionId: pj.publishedVersionId,
      teacherName: tp?.fullName || '',
      teacherImage: tp?.legacyAvatar || null,
      teacherAvatarBlobId: tp?.avatarBlobId || null,
    };
  }

  /**
   * اطلاعات یک درس خاص برای LessonStepsPage
   * جستجو با legacyLessonId (مثل 'Lesson1')
   */
    getLessonData(grade, subject, folderName, legacyLessonId, field = null) {
    // 🔽 CHANGED: پاس‌دادن field
    const bookData = this.getBookData(grade, subject, folderName, field);
    if (!bookData) return null;

    let lesson = null;
    let globalIndex = 0;
    for (const ch of bookData.chapters) {
      for (const ls of (ch.lessons || [])) {
        const candidate = ls.legacyLessonId || `Lesson${globalIndex + 1}`;
        if (candidate === legacyLessonId) { lesson = ls; break; }
        globalIndex++;
      }
      if (lesson) break;
    }

    if (!lesson) return null;
    return {
      lesson,
      bookData,
      project: dataEngine.project.get(bookData.projectId),
      teacher: dataEngine.teacher.get(bookData.teacherId),
    };
  }

  /** آمار کل سیستم (برای داشبورد admin) */
  getSystemStats() {
    const teachers = dataEngine.teacher.list();
    const requests = dataEngine.request.list();
    const projects = dataEngine.project.list({ type: 'book' }); // 🆕 فقط کتاب‌ها

    return {
      teachers: {
        total: teachers.length,
        active: teachers.filter((t) => t.isActive).length,
        legacy: teachers.filter((t) => t.isLegacy).length,
      },
      requests: {
        total: requests.length,
        pending: requests.filter((r) => r.status === REQUEST_STATUS.PENDING).length,
        editRequested: requests.filter((r) => r.status === REQUEST_STATUS.EDIT_REQUESTED).length,
        approved: requests.filter((r) => r.status === REQUEST_STATUS.APPROVED).length,
        rejected: requests.filter((r) => r.status === REQUEST_STATUS.REJECTED).length,
        withdrawn: requests.filter((r) => r.status === REQUEST_STATUS.WITHDRAWN).length,
      },
      projects: {
        total: projects.length,
        published: projects.filter((p) => p.state === PROJECT_STATE.PUBLISHED).length,
        building: projects.filter((p) => p.state === PROJECT_STATE.BUILDING).length,
        pendingInitial: projects.filter((p) => p.state === PROJECT_STATE.PENDING_INITIAL).length,
        updatePending: projects.filter((p) => p.state === PROJECT_STATE.UPDATE_PENDING).length,
        suspended: projects.filter((p) => p.state === PROJECT_STATE.SUSPENDED).length,
      },
    };
  }

    projectKeyExists(grade, subject, folderName, field = null) {
    // 🔽 CHANGED: پاس‌دادن field
    return !!dataEngine.project.getByKey(grade, subject, folderName, field);
  }

  /** آیا این کاربر می‌تونه برای این (grade, subject) درخواست بده؟ */
    canRequestSubject(userId, grade, subject, field = null) {
    const g = normalizeGrade(grade);
    const s = normalizeSubject(subject);
    const f = field ? normalizeField(field) : null;
    if (!g || !s) return { ok: false, error: 'invalid_grade_or_subject' };

    // 🔽 CHANGED: اعتبارسنجی field بر اساس پایه
    if (needsField(g) && !f) return { ok: false, error: 'field_required' };
    if (!needsField(g) && f) return { ok: false, error: 'field_not_allowed' };

    // چک pending requests — با field
    const pending = dataEngine.request
      .list({ userId, status: [REQUEST_STATUS.PENDING, REQUEST_STATUS.EDIT_REQUESTED] })
      .find((r) =>
        String(r.grade) === g &&
        r.subject === s &&
        (r.field || null) === f                // 🔽 CHANGED
      );
    if (pending) return { ok: false, error: 'pending_request_exists', requestId: pending.id };

    // چک معلم قبلی — با field
    const tp = dataEngine.teacher.getByUserId(userId);
    if (tp) {
      const existingProject = dataEngine.project.list({
        teacherId: tp.id, grade: g, subject: s, field: f,   // 🔽 CHANGED
      })[0];
      if (existingProject) {
        return { ok: false, error: 'already_teacher_for_subject', projectId: existingProject.id };
      }
    }
    return { ok: true };
  }

  /** لیست همه‌ی معلم‌های یک کاربر */
  getAllTeacherProfilesForUser(userId) {
    return dataEngine.teacher.list().filter((t) => t.userId === userId);
  }

  /** لیست همه‌ی درخواست‌های یک کاربر (برای صفحه‌ی "درخواست‌های من") */
  getUserRequestHistory(userId) {
    return dataEngine.request.list({ userId });
  }
}

// ================================================================
// SECTION 13: MIGRATION
// ================================================================

export class Migration {
  /**
   * اجرای migration از داده‌های legacy
   * @param {object} legacyData - { teachersData, courseDetails }
   *   teachersData: { '1-riazi': [{ id, name, folderName, ... }] }
   *   courseDetails: { '1-riazi': { title, description } }
   * @param {object} opts - { force }
   */
  async migrate(legacyData = {}, { force = false } = {}) {
    const flag = engine.get(KEYS.MIGRATION_FLAG, null);
    if (flag && !force) return { ok: true, skipped: true, at: flag.at };

    const { teachersData = {}, courseDetails = {} } = legacyData;

    // snapshot قبل از migration
    try { engine.createBackup({ tag: 'pre-migration' }); }
    catch (e) { console.warn('[Migration] backup failed:', e); }

    const stats = {
      teachersCreated: 0,
      teachersMatched: 0,
      projectsCreated: 0,
      projectsMatched: 0,
      errors: [],
    };

    // ۱. پردازش TEACHERS_DATA
    for (const [key, teachers] of Object.entries(teachersData)) {
      const parts = key.split('-');
      if (parts.length !== 2) continue;
      const grade = normalizeGrade(parts[0]);
      const subject = normalizeSubject(parts[1]);
      if (!grade || !subject) continue;

      for (const t of teachers) {
        if (!t.folderName) continue;

        // پیدا یا ساخت TeacherProfile
        let tp = dataEngine.teacher.getByFolderName(t.folderName);
        if (!tp) {
          const legacyUserId = `legacy_teacher_${t.id || t.folderName}`;
          const res = dataEngine.teacher.create({
            userId: legacyUserId,
            folderName: t.folderName,
            fullName: t.name || 'معلم',
            bio: t.fullDescription || t.description || '',
            experienceYears: 0,
            specialties: [],
            avatarBlobId: null,
            isActive: true,
            isLegacy: true,
            legacyId: t.id || null,
            legacyAvatar: t.image || null,
            legacyStars: t.stars || 0,
            legacyStudentsRated: t.studentsRated || 0,
            legacyIsSpecial: !!t.isSpecial,
          });
          if (!res.ok) {
            stats.errors.push({ kind: 'teacher', folder: t.folderName, errors: res.errors });
            continue;
          }
          tp = res.data;
          stats.teachersCreated++;
        } else {
          stats.teachersMatched++;
        }

        // پیدا یا ساخت Project
                // پیدا یا ساخت Project
        let pj = dataEngine.project.getByKey(grade, subject, t.folderName);
        if (!pj) {
          const cd = courseDetails[key] || {};
          // 🔽 CHANGED: عنوان از curriculum
          const defaultTitle = getBookTitle(grade, null, subject)
            || `${SUBJECT_LABELS[subject]} - ${GRADE_TITLES[grade]}`;
          const res = dataEngine.project.create({
            teacherId: tp.id,
            grade,
            subject,
            folderName: t.folderName,
            title: cd.title ? `${cd.title} - ${GRADE_TITLES[grade]}` : defaultTitle,
            description: cd.description || '',
            state: PROJECT_STATE.PUBLISHED,
            isLegacy: true,
            legacySource: { grade, subject, folderName: t.folderName, key },
            publishedAt: now(),
          });
          if (!res.ok) {
            stats.errors.push({ kind: 'project', key, errors: res.errors });
            continue;
          }
          pj = res.data;
          stats.projectsCreated++;
        } else {
          stats.projectsMatched++;
        }

        // ثبت آمار legacy روی پروژه
        if (pj) {
          dataEngine.project.update(pj.id, {
            stats: {
              ...pj.stats,
              avgRating: tp.legacyStars || 0,
              ratingsCount: tp.legacyStudentsRated || 0,
              isSpecial: tp.legacyIsSpecial || false,
            },
          });
        }
      }
    }

    engine.set(KEYS.MIGRATION_FLAG, { at: now(), version: DATA_SCHEMA, stats });
    return { ok: true, stats };
  }

  isNeeded() { return !engine.get(KEYS.MIGRATION_FLAG, null); }

  getStatus() { return engine.get(KEYS.MIGRATION_FLAG, null); }

  reset() { engine.remove(KEYS.MIGRATION_FLAG); }
}

// ================================================================
// SECTION 14: SINGLETON INSTANCES + FACADE
// ================================================================

export const teacherRegistry = new TeacherRegistry();
export const requestRegistry = new RequestRegistry();
export const projectRegistry = new ProjectRegistry();
export const categoryRegistry = new ProjectCategoryRegistry(); // 🆕
export const versionRegistry = new VersionRegistry();
export const migration = new Migration();
export const query = new Query();

export const dataEngine = {
  teacher: teacherRegistry,
  request: requestRegistry,
  project: projectRegistry,
  category: categoryRegistry, // 🆕
  version: versionRegistry,
  migration,
  query,
  factories: {
    createTeacherProfile,
    createTeacherRequest,
    createProject,
    createProjectVersion,
    createChapter,
    createLesson,
    createStep,
    createVideoRef,
    createFreeStudyVideo, // 🆕
  },
  validators: {
    validateTeacherProfile,
    validateTeacherRequest,
    validateProject,
    validateProjectVersion,
    validateChapter,
    validateLesson,
    validateStep,
    validateVideoRef,
    validateFolderName,
    validateFreeStudyVideo, // 🆕
  },
    utils: {
    genTeacherId, genRequestId, genProjectId, genVersionId,
    genChapterId, genLessonId, genStepId, genVideoId,
    genFreeStudyVideoId, // 🆕
    generateFolderName,
    normalizeGrade, normalizeSubject, normalizeField,
    resequence,
    isQuizStep, isLessonStep,
  },
  constants: {
    DATA_SCHEMA, GRADES, GRADE_TITLES, SUBJECT_LABELS, SUBJECTS_BY_GRADE,
    REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
    PROJECT_TRANSITIONS, REQUEST_TRANSITIONS,
    FIELDS, FIELD_LIST, FIELD_IDS, SUBJECTS,
    STEP_TYPE,  // 🆕
  },
  // 🔽 CHANGED: curriculum lookup helpers برای UI
  curriculum: {
    getGrade, getField, getSubject,
    getSubjectTitle, getBookTitle,
    isHighSchool, needsField,
    getSubjectIdsFor, getSubjectsFor, getStageForGrade,
  },
  /** برای تست‌ها: پاک‌سازی کامل */
  resetAll() {
    teacherRegistry.clear();
    requestRegistry.clear();
    projectRegistry.clear();
    categoryRegistry.clear(); // 🆕
    versionRegistry.clear();
    engine.remove(KEYS.TEACHER_BY_USER);
    engine.remove(KEYS.TEACHER_BY_FOLDER);
    engine.remove(KEYS.PROJECT_BY_TEACHER);
    engine.remove(KEYS.PROJECT_BY_KEY);
    engine.remove(KEYS.REQUEST_IDEMPOTENCY);
    migration.reset();
  },
};

// 🆕 متد عمومی برای بازسازی ایندکس‌ها
dataEngine.rebuildAllIndexes = function () {
  try {
    teacherRegistry._rebuildIndexes();
    projectRegistry._rebuildIndexes();
    console.log('[dataEngine] all indexes rebuilt');
  } catch (e) {
    console.warn('[dataEngine] rebuild failed:', e);
  }
};

export default dataEngine;